import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Tab, TabList, Tabs } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../components/carbon/DataTable/DataTable";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";
import TagForm from "./TagForm";
import BulkAssignTagsTab from "./BulkAssignTagsTab";
import { useGetTagsPaginated, useDeleteTag } from "../../hooks/useTags";
import useDebounce from "../../hooks/useDebounce";
import { useNotification } from "../../context";
import { TagResponse } from "../../types";
import "./tags.scss";

// TagController's own `/tags/paginated` default (`@RequestParam(defaultValue = "20") Integer
// size` — TagController.java) — matched here rather than reusing Sites/Users' 10, since the
// brief calls for staying consistent with THIS endpoint's own default, not the sibling flows'.
const PAGE_SIZE = 20;

interface TagRow extends DataTableRow {
  id: string;
  name: ReactNode;
  color: ReactNode;
  description: string;
  actions: ReactNode;
}

/**
 * Split out of the pre-Carbon `TagsManagement.tsx` (1,101 lines) — this file owns the LIST (on
 * `PageHeader`/`DataTable`/`EmptyState`, following `containers/Sites/Sites.tsx` as the reviewed
 * template) and wires the create/edit form (`TagForm.tsx`, Carbon `ComposedModal`) and delete
 * (`ConfirmDialog`). Task 5's own bulk-assign rebuild is the other half of the old screen — kept
 * alive here, unmigrated, as `BulkAssignTagsTab.tsx` behind the second tab (see that file's own
 * docstring for why it's still Tailwind).
 *
 * No Status column: `Tag.active` is dead — Task 2 controller-verified the only write site is the
 * soft-delete path (`TagService.deleteTag` forces it false), no repository query filters on it,
 * and no FE code reads it to render anything, unlike Sites' real `active`-gated activation UI.
 *
 * Server-side search + pagination via `GET /tags/paginated?query=` (Task 2's backend addition,
 * wired into `useGetTagsPaginated` by this task — see hooks/useTags.ts's own comment on why the
 * existing `tagKeys.all`-based mutation invalidation didn't need to change for this).
 */
const Tags = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();

  const [selectedTab, setSelectedTab] = useState(0);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const { debouncedSearchTerm } = useDebounce(searchTerm, 400);

  const [formOpen, setFormOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<TagResponse | null>(null);
  const [deletingTag, setDeletingTag] = useState<TagResponse | null>(null);

  // A new search always restarts at the first page — same convention Sites.tsx/Users.tsx use.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm]);

  const { data: tagsPage, isLoading } = useGetTagsPaginated(
    page,
    PAGE_SIZE,
    debouncedSearchTerm
  );

  const visibleTags = useMemo(() => tagsPage?.content ?? [], [tagsPage]);

  const { mutate: deleteTag, isPending: isDeleting } = useDeleteTag(
    () => {
      openNotification(t("tags.tagDeleted"));
      setDeletingTag(null);
    },
    () => openNotification(t("tags.deleteError"))
  );

  // Genuinely nothing to manage yet, as opposed to a search that merely narrowed the roster to
  // nothing — same `isTrulyEmpty` convention Sites.tsx/Users.tsx use, keyed off the debounced
  // term rather than an in-memory array (there is none any more).
  const isTrulyEmpty =
    !isLoading && visibleTags.length === 0 && !debouncedSearchTerm;

  const openCreateModal = () => {
    setEditingTag(null);
    setFormOpen(true);
  };

  const openEditModal = (tag: TagResponse) => {
    setEditingTag(tag);
    setFormOpen(true);
  };

  const addAction = (
    <Button onClick={openCreateModal} renderIcon={Add}>{t("tags.createNew")}</Button>
  );

  const rows: TagRow[] = useMemo(
    () =>
      visibleTags.map((tag) => ({
        id: tag.id,
        // The clickable/edit affordance the brief calls for: clicking the name itself opens the
        // same edit modal the row's "Edit Tag" action does.
        name: (
          <button
            type="button"
            className="tags__name-link"
            onClick={() => openEditModal(tag)}
          >
            {tag.name}
          </button>
        ),
        // Colour is DATA here, not decoration — an inline `style` on the swatch is the brief's
        // own guidance, same as the pre-Carbon screen's identical pairing.
        color: (
          <div className="tags__color-cell">
            <span
              className="tags__color-swatch"
              style={{ backgroundColor: tag.color }}
            />
            <span>{tag.color}</span>
          </div>
        ),
        description: tag.description || "-",
        actions: (
          <div className="tags__actions-cell">
            <Button kind="ghost" size="sm" onClick={() => openEditModal(tag)}>
              {t("tags.editTag")}
            </Button>
            <Button
              kind="danger--ghost"
              size="sm"
              onClick={() => setDeletingTag(tag)}
            >
              {t("tags.deleteTag")}
            </Button>
          </div>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleTags, t]
  );

  const headers: DataTableColumn<TagRow>[] = useMemo(
    () => [
      { key: "name", header: t("tags.tagName") },
      { key: "color", header: t("tags.color") },
      { key: "description", header: t("tags.description") },
      { key: "actions", header: "" },
    ],
    [t]
  );

  // Two empty states (Users/Sites pattern): distinguished off the UNFILTERED/no-query condition
  // (`debouncedSearchTerm` empty), not off the filtered result being empty — a search that
  // narrows to zero rows and a company with zero tags need different copy and different actions.
  const empty = debouncedSearchTerm ? (
    <EmptyState
      title={t("tags.noResults")}
      action={
        <Button kind="ghost" onClick={() => setSearchTerm("")}>
          {t("table.clearSearch")}
        </Button>
      }
    />
  ) : (
    <EmptyState
      title={t("tags.noTags")}
      description={t("tags.noTagsDescription")}
      action={addAction}
    />
  );

  return (
    <div>
      <PageHeader
        title={t("tags.manageTags")}
        action={selectedTab === 0 && !isTrulyEmpty ? addAction : null}
      />

      <div className="tags__tabs">
        <Tabs
          selectedIndex={selectedTab}
          onChange={({ selectedIndex }) => setSelectedTab(selectedIndex)}
        >
          <TabList aria-label={t("tags.manageTags")}>
            <Tab>{t("tags.manageTags")}</Tab>
            <Tab>{t("tags.bulkAssign")}</Tab>
          </TabList>
        </Tabs>
      </div>

      {selectedTab === 1 ? (
        <BulkAssignTagsTab />
      ) : (
        <DataTable
          headers={headers}
          rows={rows}
          isLoading={isLoading}
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            label: t("tags.search"),
          }}
          pagination={{
            // DataTable's Pagination (Carbon) is 1-indexed; this screen's own paging state — and
            // the paginated-tags query it drives — stays 0-indexed to match the API, same
            // boundary conversion Sites.tsx/Users.tsx use.
            page: page + 1,
            pageSize: PAGE_SIZE,
            totalItems: tagsPage?.totalElements ?? 0,
            onPageChange: (newPage) => setPage(newPage - 1),
          }}
          empty={empty}
        />
      )}

      <TagForm tag={editingTag} open={formOpen} setOpen={setFormOpen} />

      {/* Delete is SOFT (Task 2 controller-verified: TagService.deleteTag sets `deleted=true`,
          the row is never removed) — `tags.deleteBody` says the tag is removed from materials, it
          does not claim the destructive-and-irreversible language the pre-Carbon screen's
          `deleteWarning` copy used ("This action cannot be undone"), which the backend does not
          actually enforce. */}
      <ConfirmDialog
        open={deletingTag !== null}
        danger
        title={t("tags.deleteTag")}
        body={
          deletingTag ? t("tags.deleteBody", { name: deletingTag.name }) : ""
        }
        confirmLabel={t("cta.delete")}
        onConfirm={() => deletingTag && deleteTag(deletingTag.id)}
        onCancel={() => setDeletingTag(null)}
        isPending={isDeleting}
      />
    </div>
  );
};

export default Tags;
